import {Type} from "class-transformer";

import {serializeType} from "./common";
import {User} from "./user";
import {Clearance} from "./clearance";
import {EmployeeTermination} from "./employeeTermination";
import {PayeeSettings} from "./payee-settings";
import {EmployeeClass} from "./employee-class";
import {FlsaType} from "../enums/flsa-type.enum";
import {RefCode} from "./ref-code";

export enum ClearanceStatusType {
  Active = 'Active', InActive = 'InActive', Suspended = 'Suspended'
}

export enum ContactType {
  Primary = 'Primary', Secondary = 'Secondary', Alternative = 'Alternative'
}

export enum RelationshipType {
  Spouse = 'Spouse', Child = 'Child', Neighbor = 'Neighbor', Friend = 'Friend', Other = 'Other'
}

export enum EmailType {
  Work = 'WORK', Personal = 'PERSONAL'
}

export enum PhoneType {
  Office = 'Office', Home = 'Home', Mobile = 'Mobile'
}

export enum CycleType {
  Weekly = 'Weekly', BiWeelkly = 'BiWeekly', Monthly = 'Monthly', SemiMonthly = 'SemiMonthly'
}

export enum QleType {
  ChangeInHouseHoldSize = 'CHANGE_IN_HOUSEHOLD_SIZE', ChangeInResidence = 'CHANGE_IN_RESIDENCE', LossOfCoverage = 'LOSS_OF_COVERAGE', Other = 'OTHER'
}

export enum AppraisalStatus {
  CREATED = 'CREATED', OPEN = 'OPEN', CLOSED = 'CLOSED'
}

export enum SignedStatus {
  All = "ALL", Signed = "SIGNED", NotSigned = "NOT_SIGNED"
}

export enum AppraisalAccessedBy {
  Hr = "HR",
  Manager = "MANAGER",
  HigherManager = "HIGHER_MANAGER",
  Self = "SELF",
  Other = "OTHER"
}

// ToDo consolidate with Sol Mgr
export class RefDomain {
  id: number;
  active: boolean;
  cached: boolean;
  description: string;
  name: string;
  inherited: boolean;
  overridable: boolean;
  version: number;
  orgId: number;
  orgName: string;
  @Type(serializeType(RefCode)) refCodes?: Array<RefCode>;
}

export enum DependentType {
  Spouse = 'Spouse', Child = 'Child', Other = 'Other'
}

export class Dependent {
  id: number;
  userId: number;
  dateOfBirth: string;
  type: DependentType;
  firstName: string;
  lastName: string;
  middleName: string;
  ssn: string;
  version: number;
}

//Equivalent to UserAddress in Backend
export class EmployeeAddress{

  id: number;
  addrName?: string;
  locationCode?: string
  addrLn1: string;
  addrLn2: string;
  city: string;
  stateCode: string;
  postalCode: string;
  country: string;

  current: boolean;
  effectiveOn: string;
  userId: number;
}

export class EmergencyContactEmail {
  id: number;
  contactId: number;
  type: string;
  address: string;
  version: number;
}

export class EmergencyContactPhone {
  id: number;
  contactId: number;
  type: string;
  number: string;
  version: number;
}

export class EmergencyContact {
  id: number;
  relationType: string;
  type: string;
  firstName: string;
  lastName: string;
  company: string;
  @Type(serializeType(EmergencyContactEmail)) emails?: Array<EmergencyContactEmail>;
  @Type(serializeType(EmergencyContactPhone)) phones?: Array<EmergencyContactPhone>;

  emailType?: string;     // for display, need to remove it when sending it to backend
  emailAddr?: string;     // for display, need to remove it when sending it to backend
  phoneType?: string;     // for display, need to remove it when sending it to backend
  phoneNumber?: string;   // for display, need to remove it when sending it to backend
}

export enum CredentialType {
  Certification = 'Certification', Graduate = 'Graduate', Training = 'Training', UnderGraduate = 'UnderGraduate'
}

export enum CredentialStatusType {
  Active = 'Active', InActive = 'InActive', Suspended = 'Suspended'
}

export class Credential {
  id: number;
  userId: number;
  type: CredentialType;
  status: CredentialStatusType;
  credentialGranted: string;
  credentialGrantedOn: string;
  degreeMajor: string;
  grantingInstitution: string;
  credentialId: number;
  version: number;
}

export class W4Deduction {
  id: number;
  userId: number;
  dedChildCredit: number;
  dedDependents: number;
  dedHousehold: number;
  dedMaritalStatus: number;
  dedSpouse: number;
  dedYourself: number;
  dedTotalCredit: number;
  effectiveOn: string;
  taxYear: string;
  version: number;
}

export class EmployeeAccountAssignment {
  id: number;
  userId: number;
  fringePoolId: number;
  gandaPoolId?: number;
  overheadPoolId?: number;
  materialHandlePoolId?: number;
  version: number;
}

export class PayeeACH {
  id: number;
  version: number;
  active: boolean = true;
  garnishment: boolean = false;
  nickName: string;
  bankName: string;
  accountType: string;
  routingNumber: string;
  accountNumber: string;
  effectiveOn: Date;
  allocationType: string;
  allocationPercent: number;
  allocationAmount: number;
  payeeId: number;
  achFundingSourceDetails: any;
}

export class Garnishment {
  id: number;
  userId: number;
  active: boolean;
  payeeACHId: number;
  courtOrderNumber: string;
  courtOrderJudge: string;
  paymentAmount: number;
  paymentFrequency: CycleType;
  paymentStartOn: string;
  paymentEndOn: string;
  recipientName: string;
  disbursementType: string;
  addrLn1: string;
  addrLn2: string;
  city: string;
  stateCode: string;
  postalCode: string;
  country: string = "USA";
  @Type(serializeType(PayeeACH)) payeeACH: PayeeACH;
}

export class SalaryHistory {
  id: number;
  highPointSalary: number;
  lowPointSalary: number;
  effectiveOn: string;
  annualSalary?: number;
  hourlySalary?: number;
  userId: number;
  positionDescriptionId: number;
  positionDescriptionTitle: number;
}

export class Employee extends User {
  id: number;
  userId: number;
  fullName: string;
  subtype: string;
  active: boolean;
  firstName: string;
  lastName: string;
  middleName: string;
  version: number;
  workEmail: string;
  personalEmail: string;
  officePhone: string;
  disabled: boolean;
  employeeClassId: number;
  employeeClass?: EmployeeClass;
  hasClearance: boolean;
  isDirect: boolean;
  locationId: number;
  loginId: string;
  mobilePhone: string;
  obsId: number;
  obsName?: string;
  obsManager?: boolean;
  rootObs?: boolean;
  employmentType: string;
  orgId: number;
  timesheetPeriod: number;
  finalPayrollCheckDate: string;

  homeObsId?: number;
  homeObsName?: string;
  resourceManagerId?: number;
  resourceManagerName?: string;

  title: string;
  approvalId: number;
  leaveType?: string;
  veteran: boolean;
  positionDescriptionId: number;
  hoursPerWeek?: number;
  initialReviewPeriod: number;
  hourlySalary: number;
  scaEmployeeClass: boolean;
  @Type(serializeType(EmployeeAccountAssignment)) accountAssignments?: Array<EmployeeAccountAssignment>;
  @Type(serializeType(EmployeeAddress)) currentAddress?: EmployeeAddress;
  @Type(serializeType(Dependent)) dependents?: Array<Dependent>;
  @Type(serializeType(Credential)) credentials?: Array<Credential>;
  @Type(serializeType(EmergencyContact)) emergencyContacts?: Array<EmergencyContact>;
  @Type(serializeType(W4Deduction)) w4Deductions?: Array<W4Deduction>;
  @Type(serializeType(EmployeeAddress)) addresses?: Array<EmployeeAddress>;
  @Type(serializeType(Clearance)) clearances?: Array<Clearance>;
  @Type(serializeType(EmployeeTermination)) terminations?: Array<EmployeeTermination>;
  @Type(serializeType(Garnishment)) garnishments?: Array<Garnishment>;
  @Type(serializeType(PayeeSettings)) payeeSettings?: Array<PayeeSettings>;
  @Type(serializeType(SalaryHistory)) salaryHistories?: Array<SalaryHistory>;

}

export class UserPhoto {
  id: number;
  contentType: string;
  userId: number;
  version: number;
  data: string;
}

export class EmployeeAppraisalHistory {
  id: number;
  version: number;
  userId: number;
  appraisalId: number;
  appraisalYear: string;
  appraisalFrom: string;
  appraisalThru: string;
  employeeRatingName: string;
  employeeRatingScore: number;
  managerRatingName: string;
  managerRatingScore: number;
  employeeSummary: string;
  managerSummary: string;
}

export class OrgAppraisalRating {
  id: number;
  name: string;
  ordinal: number;
  description: string;
  appraisalPeriodId: number;
  orgId: number;
}

export class OrgAppraisalCategory {
  id: number;
  version: number;

  active: boolean;
  name: string;
  description: string;
  employeeClassId: number;
  employeeClassName?: string;
  appraisalPeriodId: number;
  orgId: number;
  weight: number;
}

export class EmployeeAppraisalRating {
  id: number;
  employeeDescription: string;
  managerDescription: string;
  employeeRatingId: number;
  managerRatingId: number;
  version: number;
  @Type(serializeType(OrgAppraisalCategory)) category?: OrgAppraisalCategory;
}

export class OrgAppraisalPeriod {
  id: number;
  name: string;
  dateFrom: string;
  dateThru: string;
  selfDueDate: string;
  managerDueDate: string;
  affectsSalary: boolean;
  isFinal: boolean;
  orgId: number;
  appraisalStatus: AppraisalStatus;
}

export class EmployeeAppraisalSummary {
  id?: number;

  appraisalYear: number;
  appraisalPeriodId: number;
  dateFrom: string;
  dateThru: string;

  userId: number;
  employeeName: string;
  flsaType: FlsaType;
  employeeCode: string;
  employeeObs: string;

  managerId: number;
  managerName: string;

  employeeRatingName: string;
  employeeRatingScore: number;
  managerRatingName: string;
  managerRatingScore: number;

  currentPositionId: number;
  currentPositionTitle: string;
  newPositionId: number;
  newPositionTitle: string;
  currentAnnualSalary: number;
  newAnnualSalary: number;
  currentHourlySalary: number;
  newHourlySalary: number;

  salaryRank: string;
  recommendedPercentIncrease: number;
  percentIncrease: number;
  amountIncrease: number;
  bonus: number;

  signedByUser: boolean;
  signedByUserOn: string;
  signedByManager: boolean;
  signedByManagerOn: string;
}

export class EmployeeAppraisal {
  id?: number;
  version?: number;

  appraisalYear: number;              // transient
  appraisalPeriodId: number;
  dateFrom: string;                   // transient
  dateThru: string;                   // transient

  userId: number;
  employeeName: string;               // transient
  employeeCode: string;               // transient
  flsaType: FlsaType;

  managerId: number;
  managerName: string;

  employeeRatingId: number;
  employeeRatingName: string;
  employeeRatingScore: number;
  managerRatingId: number;
  managerRatingName: string;
  managerRatingScore: number;

  currentPositionId: number;
  newPositionId: number;
  currentPositionTitle: string;
  newPositionTitle: string;
  currentAnnualSalary: number;
  newAnnualSalary: number;
  currentHourlySalary: number;
  newHourlySalary: number;
  percentIncrease: number;
  bonus: number;

  employeeSummary: string;
  managerSummary: string;

  signedByUser: string;
  signedByUserOn: Date;
  signedByManager: string;
  signedByManagerOn: Date;
  lastAppraisalOn: string;

  @Type(serializeType(EmployeeAppraisalRating)) ratings?: Array<EmployeeAppraisalRating>;
}

export class EmployeeAppraisalLog {
  id: number;
  appraisalId: number;
  positionId: number;
  positionTitle: string;
  percentIncrease: number;
  bonus: number;
  updatedByDepartment: string;
  updatedByName: string;
  updatedOn: Date;
}

export class PositionGrade {
  id: number;
  name: string;
  description: string;
  minSalary: number;
  maxSalary: number;
  orgId: number;
}


export class TrainingHistory {
  id: number;
  billable: boolean;
  description: string;
  startOn: string;
  endOn: string;
  cost: number;
  userId: number;
  version: number;
}

export class BenefitHistory {
  id: number;
  version: number;
  userId: number;
  changeOn: string;
  qleType: QleType;
  qualifyingLifeEvent: string;
  // healthDentalPlan:
}
